/**
 * UI preferences. Separate store from the document so that changing the theme
 * never touches the resume's `updatedAt` or pushes an undo entry.
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { LetterTone as LetterToneId } from '@/engine/coverLetter'

export type ThemeMode = 'light' | 'dark' | 'system'
export type EditorTab =
  | 'personal' | 'summary' | 'skills' | 'experience' | 'projects'
  | 'education' | 'certifications' | 'extras' | 'design' | 'sections'

export type ToolId =
  | 'ats' | 'keywords' | 'bullets' | 'achievements' | 'summary'
  | 'completeness' | 'duplicates' | 'length' | 'plaintext' | 'coverletter' | 'prompts'

/** What the builder's main column is showing. The sidebar drives this. */
export type BuilderMode = 'dashboard' | 'edit' | 'analyze'

interface UiState {
  theme: ThemeMode
  highContrast: boolean
  builderMode: BuilderMode
  editorTab: EditorTab
  activeTool: ToolId
  /** Mobile: the editor and preview share the viewport. */
  mobilePane: 'edit' | 'preview' | 'analyze'
  previewZoom: number
  jobDescription: string
  /**
   * Cover-letter fields. Kept here beside `jobDescription` because they belong to
   * the *application* the user is working on, not to the resume document — the
   * same master resume goes to twenty companies, and none of their names belong
   * in it. Persisted for the same reason the job description is: retyping the
   * company on every visit is friction with no upside. Never leaves the device.
   */
  letter: { company: string; role: string; hiringManager: string; tone: LetterToneId }

  setBuilderMode: (mode: BuilderMode) => void
  setTheme: (theme: ThemeMode) => void
  toggleHighContrast: () => void
  setEditorTab: (tab: EditorTab) => void
  setActiveTool: (tool: ToolId) => void
  setMobilePane: (pane: UiState['mobilePane']) => void
  setPreviewZoom: (zoom: number) => void
  setJobDescription: (jd: string) => void
  setLetter: (patch: Partial<UiState['letter']>) => void
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      theme: 'system',
      highContrast: false,
      builderMode: 'dashboard',
      editorTab: 'personal',
      activeTool: 'ats',
      mobilePane: 'edit',
      previewZoom: 1,
      jobDescription: '',
      letter: { company: '', role: '', hiringManager: '', tone: 'professional' },

      setBuilderMode: (builderMode) => set({ builderMode }),
      setTheme: (theme) => set({ theme }),
      toggleHighContrast: () => set((s) => ({ highContrast: !s.highContrast })),
      setEditorTab: (editorTab) => set({ editorTab }),
      setActiveTool: (activeTool) => set({ activeTool }),
      setMobilePane: (mobilePane) => set({ mobilePane }),
      setPreviewZoom: (previewZoom) => set({ previewZoom }),
      setJobDescription: (jobDescription) => set({ jobDescription }),
      setLetter: (patch) => set((s) => ({ letter: { ...s.letter, ...patch } })),
    }),
    {
      name: 'ats-resume:ui',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        theme: s.theme,
        highContrast: s.highContrast,
        previewZoom: s.previewZoom,
        // The pasted job description is the user's most re-used input; keeping it
        // saves them re-pasting on every visit. It never leaves the device.
        jobDescription: s.jobDescription,
        letter: s.letter,
      }),
    },
  ),
)

/** Applies the theme to <html>. Kept out of React so the inline boot script and
 *  the store use exactly one code path. */
export const applyTheme = (theme: ThemeMode, highContrast: boolean): void => {
  if (typeof document === 'undefined') return
  const prefersDark =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches
  const resolved = theme === 'system' ? (prefersDark ? 'dark' : 'light') : theme
  const daisyTheme = highContrast ? (resolved === 'dark' ? 'atsdark-hc' : 'atslight-hc') : resolved === 'dark' ? 'atsdark' : 'atslight'
  document.documentElement.setAttribute('data-theme', daisyTheme)
  document.documentElement.style.colorScheme = resolved
}
